import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LOGIN } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import { registerEmail, loginEmail, googleCodeExchange } from "@/lib/api";
import { PartnerBanner } from "@/components/PartnerBanner";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (d?.msg) return d.msg;
  return err?.message || "Something went wrong. Please try again.";
}

export default function Login() {
  const heroImg =
    "https://images.unsplash.com/photo-1769886250520-ce0f9abbcc9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwbmF0dXJlJTIwc3VucmlzZXxlbnwwfHx8fDE3ODM1NzY4MDF8MA&ixlib=rb-4.1.0&q=85";

  const [mode, setMode] = useState("signin"); // "signin" | "signup"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }
    if (mode === "signup" && password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const res =
        mode === "signup"
          ? await registerEmail({ email, password, name })
          : await loginEmail({ email, password });
      await setUser(res.user);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setError("");
  };

  // ---- Google Identity Services (popup code flow) ----
  const googleClientRef = useRef(null);
  const [gsiReady, setGsiReady] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    if (window.google?.accounts?.oauth2) {
      initClient();
      return;
    }
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.onload = () => initClient();
    document.body.appendChild(s);
    function initClient() {
      googleClientRef.current = window.google.accounts.oauth2.initCodeClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: "openid email profile",
        ux_mode: "popup",
        callback: async (response) => {
          if (!response?.code) {
            setError("Google sign-in was cancelled.");
            setGoogleLoading(false);
            return;
          }
          try {
            const res = await googleCodeExchange(response.code);
            await setUser(res.user);
            navigate("/dashboard");
          } catch (err) {
            setError(formatApiError(err));
          } finally {
            setGoogleLoading(false);
          }
        },
        error_callback: (err) => {
          setGoogleLoading(false);
          if (err?.type === "popup_closed") return;
          setError(err?.message || "Google sign-in failed.");
        },
      });
      setGsiReady(true);
    }
  }, [setUser, navigate]);

  const startGoogle = () => {
    setError("");
    if (!GOOGLE_CLIENT_ID) {
      setError("Google sign-in is not configured.");
      return;
    }
    if (!gsiReady || !googleClientRef.current) {
      setError("Google sign-in is still loading, please try again.");
      return;
    }
    setGoogleLoading(true);
    googleClientRef.current.requestCode();
  };

  return (
    <div
      data-testid={LOGIN.page}
      className="min-h-screen bg-[#F7F7F4] text-[#1A1B1A] grid grid-cols-1 lg:grid-cols-2"
    >
      {/* Left column — copy */}
      <div className="flex flex-col justify-between p-8 sm:p-12 lg:p-16">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#0F3524] flex items-center justify-center">
            <span className="font-mono-data text-[#F7F7F4] text-[11px]">KW</span>
          </div>
          <div className="font-display text-lg tracking-tight">Kilowatt Ledger</div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-xl w-full"
        >
          <div className="overline mb-6">A sustainability journal · Est. 2026</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02]">
            Read your <em className="not-italic text-[#0F3524]">kilowatt-hours</em> like a broker reads the market.
          </h1>
          <p className="mt-6 text-base text-[#3d3f3c] font-body max-w-md leading-relaxed">
            Upload your monthly TNB bills. We compute Load Factor, cost efficiency,
            and CO₂ intensity — then hand you the story in plain language.
          </p>

          {/* Auth card */}
          <div className="mt-8 max-w-md border border-[#E5E5DE] bg-white/60 backdrop-blur-sm">
            {/* Tabs */}
            <div className="flex border-b border-[#E5E5DE]">
              <button
                type="button"
                data-testid={LOGIN.tabSignin}
                onClick={() => switchMode("signin")}
                className={`flex-1 px-4 py-3 text-sm font-medium tracking-tight transition-colors ${
                  mode === "signin"
                    ? "bg-[#0F3524] text-[#F7F7F4]"
                    : "text-[#3d3f3c] hover:bg-[#F0F0EA]"
                }`}
              >
                Sign In
              </button>
              <button
                type="button"
                data-testid={LOGIN.tabSignup}
                onClick={() => switchMode("signup")}
                className={`flex-1 px-4 py-3 text-sm font-medium tracking-tight transition-colors ${
                  mode === "signup"
                    ? "bg-[#0F3524] text-[#F7F7F4]"
                    : "text-[#3d3f3c] hover:bg-[#F0F0EA]"
                }`}
              >
                Create Account
              </button>
            </div>

            <form onSubmit={submit} className="p-5 space-y-3">
              {mode === "signup" && (
                <div>
                  <label className="overline mb-1 block">Name (optional)</label>
                  <input
                    type="text"
                    data-testid={LOGIN.nameInput}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full px-3 py-2.5 bg-[#F7F7F4] border border-[#E5E5DE] focus:outline-none focus:border-[#0F3524] text-sm font-body"
                  />
                </div>
              )}
              <div>
                <label className="overline mb-1 block">Email</label>
                <input
                  type="email"
                  data-testid={LOGIN.emailInput}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className="w-full px-3 py-2.5 bg-[#F7F7F4] border border-[#E5E5DE] focus:outline-none focus:border-[#0F3524] text-sm font-body"
                  required
                />
              </div>
              <div>
                <label className="overline mb-1 block">Password</label>
                <input
                  type="password"
                  data-testid={LOGIN.passwordInput}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "signup" ? "At least 8 characters" : "Your password"}
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  className="w-full px-3 py-2.5 bg-[#F7F7F4] border border-[#E5E5DE] focus:outline-none focus:border-[#0F3524] text-sm font-body"
                  required
                  minLength={mode === "signup" ? 8 : undefined}
                />
              </div>

              {error && (
                <div
                  data-testid={LOGIN.errorMsg}
                  className="text-sm text-[#B24F35] bg-[#FBEDE7] border border-[#F0D3C6] px-3 py-2"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                data-testid={LOGIN.submitBtn}
                disabled={submitting}
                className="w-full px-5 py-3 bg-[#0F3524] text-[#F7F7F4] font-medium tracking-tight
                           hover:bg-[#174D35] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {submitting
                  ? mode === "signup"
                    ? "Creating account…"
                    : "Signing in…"
                  : mode === "signup"
                  ? "Create Account"
                  : "Sign In"}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 pt-2">
                <div className="flex-1 h-px bg-[#E5E5DE]" />
                <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#6E716C]">
                  or
                </span>
                <div className="flex-1 h-px bg-[#E5E5DE]" />
              </div>

              <button
                type="button"
                data-testid={LOGIN.googleBtn}
                onClick={startGoogle}
                disabled={googleLoading}
                className="w-full px-5 py-3 bg-white border border-[#1A1B1A] text-[#1A1B1A] font-medium tracking-tight
                           hover:bg-[#F0F0EA] disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-3"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M21.35 11.1H12v2.98h5.34c-.23 1.44-1.66 4.22-5.34 4.22-3.22 0-5.85-2.67-5.85-5.95S8.78 6.4 12 6.4c1.83 0 3.06.78 3.76 1.45l2.57-2.47C16.77 3.9 14.6 3 12 3 6.94 3 2.85 7.05 2.85 12.05S6.94 21.1 12 21.1c6.93 0 8.5-6.13 7.95-9.99h-8.6z" />
                </svg>
                {googleLoading ? "Opening Google…" : "Continue with Google"}
              </button>
            </form>
          </div>

          <p className="mt-4 text-xs text-[#6E716C]">Free while in beta.</p>
        </motion.div>

        <div className="mt-16 grid grid-cols-3 gap-6 border-t border-[#E5E5DE] pt-6">
          <Stat label="CO₂ factor" value="0.585 kg / kWh" />
          <Stat label="Currency" value="MYR (RM)" />
          <Stat label="AI Model" value="Claude Sonnet 4.5" />
        </div>

        <div className="mt-10 flex flex-col items-start gap-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#6E716C]">Contact</div>
          <a
            href="mailto:alfred.initiatives@gmail.com"
            className="text-sm font-mono-data text-[#1A1B1A] hover:text-[#B24F35] transition-colors"
          >
            alfred.initiatives@gmail.com
          </a>
          <PartnerBanner className="mt-3" />
        </div>
      </div>

      {/* Right column — image */}
      <div className="relative hidden lg:block">
        <img
          src={heroImg}
          alt="Modern house nested in sand dunes at sunset"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0F3524]/40 via-transparent to-transparent" />
        <div className="absolute bottom-8 right-8 max-w-xs bg-[#F7F7F4] border border-[#E5E5DE] p-6">
          <div className="overline mb-2">Editorial ·  Issue 01</div>
          <p className="font-display text-2xl leading-tight">
            &ldquo;The most sustainable kilowatt is the one you never spend.&rdquo;
          </p>
          <div className="mt-3 text-xs text-[#6E716C] font-mono-data">— House rules</div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className="font-mono-data text-base text-[#1A1B1A]">{value}</div>
    </div>
  );
}
